import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NotificationTriggers } from "./NotificationTriggers";
import { NotificationList } from "./NotificationList";
import {
  fetchNotifications,
  fetchNotificationSettings,
  updateNotificationSettings,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "../../../features/notification/notificationSlice";

export const NotificationsPage = () => {
  const dispatch = useDispatch();
  const {
    notifications,
    unreadCount,
    pagination,
    loading,
    settings,
    settingsLoading,
    updateLoading,
    settingsError,
  } = useSelector((state) => state.notification);

  // Fetch notifications and settings on mount
  useEffect(() => {
    dispatch(fetchNotifications({ page: 1, limit: 10, role: 'superAdmin' }));
    dispatch(fetchNotificationSettings());
  }, [dispatch]);

  // Refetch settings if there's an error
  useEffect(() => {
    if (settingsError) {
      // Refetch settings after a short delay on error
      const timer = setTimeout(() => {
        dispatch(fetchNotificationSettings());
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [settingsError, dispatch]);

  // Handle trigger toggle
  const handleToggle = useCallback(
    (key) => {
      const updatedTriggers = {
        ...settings,
        [key]: !settings[key],
      };
      dispatch(updateNotificationSettings(updatedTriggers));
    },
    [dispatch, settings]
  );

  // Handle mark as read
  const handleMarkAsRead = useCallback(
    (notificationId) => {
      dispatch(markNotificationAsRead({ notificationId, role: 'superAdmin' }));
    },
    [dispatch]
  );

  // Handle mark all as read
  const handleMarkAllAsRead = useCallback(() => {
    dispatch(markAllNotificationsAsRead({ role: 'superAdmin' }));
  }, [dispatch]);

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (pagination.hasMore && !loading) {
      dispatch(
        fetchNotifications({
          page: pagination.currentPage + 1,
          limit: pagination.limit,
          role: 'superAdmin',
        })
      );
    }
  }, [dispatch, pagination, loading]);

  return (
    <div className="rounded-3xl bg-jp-card border border-jp-line shadow-sm p-6 h-full">
      <h2 className="text-jp-accent-ink text-sm font-bold uppercase tracking-widest mb-4 text-left md:text-left">
        NOTIFICATIONS
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.5fr] gap-6 h-[calc(100%-2rem)]">
        {/* Left column: triggers */}
        <div className="rounded-2xl border border-jp-line bg-jp-card p-5 flex flex-col gap-6">
          <NotificationTriggers
            triggers={settings}
            onToggle={handleToggle}
            loading={updateLoading || settingsLoading}
          />
          {/* <NotificationTemplatesEditor /> */}
        </div>

        {/* Right column: notifications list */}
        <NotificationList
          notifications={notifications}
          loading={loading}
          unreadCount={unreadCount}
          onMarkAsRead={handleMarkAsRead}
          onMarkAllAsRead={handleMarkAllAsRead}
          onLoadMore={handleLoadMore}
          hasMore={pagination.hasMore}
        />
      </div>
    </div>
  );
};
